// Multi-stem canvas waveform renderer.
//
// Draws 4 overlapping stems on a single canvas 2D context:
//   - Each stem's shape comes from a Uint8Array of per-column max-abs peaks.
//   - Per-stem amplitude = peak × slider.volume (linear scaling).
//   - Filled body at 55% alpha + 1-CSS-px solid outline in the same hex.
//   - Symmetric around a horizontal midline (bandCenter ± barHalf, matching SoundApp).
//   - Fixed z-order: iterated in stem-array order; last drawn is frontmost.
//   - Muted stem → desaturated neutral gray; any stem soloed dims the non-soloed
//     stems to 30% alpha.
//
// Redraws only when state changes (slider input, mute/solo, peaks load, resize).
// Playhead is a separate absolutely-positioned DOM element owned by the host widget.

import type { StemDef } from '@/state/models';
import type { StemGainState } from '@/audio/engine';

export interface WaveformEvents {
  seek: (timeSeconds: number) => void;
}

export type PeakData = Record<string, Uint8Array>;

const BODY_ALPHA = 0.55;
const OUTLINE_ALPHA = 1.0;
const SOLO_DIMMED_ALPHA = 0.3;
const MUTED_GRAY: [number, number, number] = [0x8a, 0x8a, 0x8a];
const DIMMED_GRAY: [number, number, number] = [0x80, 0x80, 0x80];

export class WaveformRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private events: WaveformEvents;

  private widthCss = 0;
  private heightCss = 0;
  private dpr = 1;

  private durationS = 0;
  private peaks: PeakData | null = null;
  private stems: StemDef[] = [];
  private states: StemGainState[] = [];

  private stemColorCache = new Map<string, [number, number, number]>();
  private resizeObserver: ResizeObserver | null = null;
  private rafPending = false;

  constructor(canvas: HTMLCanvasElement, events: WaveformEvents) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');
    this.ctx = ctx;
    this.events = events;

    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('keydown', this.onKeyDown);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);

    this.handleResize();
  }

  setDuration(seconds: number): void {
    this.durationS = seconds;
  }

  setPeaks(peaks: PeakData): void {
    this.peaks = peaks;
    this.scheduleRender();
  }

  setStems(stems: StemDef[]): void {
    this.stems = stems;
    this.stemColorCache.clear();
    this.scheduleRender();
  }

  setStates(states: StemGainState[]): void {
    this.states = states;
    this.scheduleRender();
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('keydown', this.onKeyDown);
    this.resizeObserver?.disconnect();
  }

  private scheduleRender(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.render();
    });
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const newW = Math.max(1, Math.round(rect.width));
    const newH = Math.max(1, Math.round(rect.height));
    const newDpr = Math.min(window.devicePixelRatio || 1, 2);

    // Skip if nothing meaningful changed — avoids clearing the canvas on
    // ResizeObserver notifications that don't actually resize us.
    if (newW === this.widthCss && newH === this.heightCss && newDpr === this.dpr) {
      return;
    }

    this.widthCss = newW;
    this.heightCss = newH;
    this.dpr = newDpr;
    this.canvas.width = this.widthCss * this.dpr;
    this.canvas.height = this.heightCss * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // Render synchronously within the same tick as the canvas clear. A
    // scheduled RAF render here would leave a blank frame between resize and
    // draw, producing a visible flash during a resize drag.
    this.render();
  }

  private resolveStemColor(stem: StemDef): [number, number, number] {
    const cached = this.stemColorCache.get(stem.name);
    if (cached) return cached;
    const styles = getComputedStyle(this.canvas);
    const raw = styles.getPropertyValue(stem.colorVar).trim();
    const rgb = parseHexColor(raw) ?? [0x80, 0x80, 0x80];
    this.stemColorCache.set(stem.name, rgb);
    return rgb;
  }

  private render(): void {
    const { ctx, widthCss: w, heightCss: h } = this;
    ctx.clearRect(0, 0, w, h);
    if (!this.peaks || this.stems.length === 0 || this.states.length === 0) return;

    const midline = h / 2;
    const halfHeight = h / 2;
    const anySoloed = this.states.some((s) => s.soloed);

    // One visual column per 2 CSS pixels, snapped to 1+ columns.
    const drawnColumns = Math.max(1, Math.round(w / 2));
    const colWidth = w / drawnColumns;
    const outlinePx = 1; // CSS px; setTransform handles DPR scaling

    for (let i = 0; i < this.stems.length; i++) {
      const stem = this.stems[i];
      const state = this.states[i];
      if (!state) continue;
      const peakBytes = this.peaks[stem.name];
      if (!peakBytes) continue;

      const srcColumns = peakBytes.length;
      const volume = state.volume;
      const muted = state.muted;
      const soloed = state.soloed;
      const dimmed = anySoloed && !soloed;

      let color: [number, number, number];
      let alphaMul: number;
      if (dimmed) {
        color = DIMMED_GRAY;
        alphaMul = SOLO_DIMMED_ALPHA;
      } else if (muted) {
        color = MUTED_GRAY;
        alphaMul = 1.0;
      } else {
        color = this.resolveStemColor(stem);
        alphaMul = 1.0;
      }

      const bodyStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${BODY_ALPHA * alphaMul})`;
      const outlineStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${OUTLINE_ALPHA * alphaMul})`;

      for (let c = 0; c < drawnColumns; c++) {
        const srcIdxStart = Math.floor((c * srcColumns) / drawnColumns);
        const srcIdxEnd = Math.max(
          srcIdxStart + 1,
          Math.floor(((c + 1) * srcColumns) / drawnColumns),
        );
        // Max-pool over the source range so coarser draw widths still use the
        // loudest peak in each pool.
        let peak = 0;
        for (let k = srcIdxStart; k < srcIdxEnd && k < srcColumns; k++) {
          if (peakBytes[k] > peak) peak = peakBytes[k];
        }
        const peakNorm = peak / 255;
        const amp = peakNorm * volume * halfHeight;
        if (amp < 0.5) continue; // below visual threshold

        const x = c * colWidth;
        ctx.fillStyle = bodyStyle;
        ctx.fillRect(x, midline - amp, colWidth, amp * 2);
        ctx.fillStyle = outlineStyle;
        ctx.fillRect(x, midline - amp, colWidth, outlinePx);
        ctx.fillRect(x, midline + amp - outlinePx, colWidth, outlinePx);
      }
    }
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.durationS <= 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frac = Math.max(0, Math.min(1, x / rect.width));
    this.events.seek(frac * this.durationS);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (this.durationS <= 0) return;
    const STEP_S = 1;
    const ariaNow = Number(this.canvas.getAttribute('aria-valuenow') || 0);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.events.seek(Math.max(0, ariaNow - STEP_S));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.events.seek(Math.min(this.durationS, ariaNow + STEP_S));
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.events.seek(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.events.seek(this.durationS);
    }
  };
}

function parseHexColor(hex: string): [number, number, number] | null {
  const trimmed = hex.replace(/^#/, '').trim();
  if (trimmed.length !== 3 && trimmed.length !== 6) return null;
  const full =
    trimmed.length === 3
      ? trimmed.split('').map((c) => c + c).join('')
      : trimmed;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return [r, g, b];
}

export function formatTimecode(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}
